import { useEffect, useRef, useState } from "react";
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Label } from "@sportscanner/ui";

// Lazily load only the Google Maps libraries we need, cached at module scope so
// the script is injected once per page even when both the picker and a second
// instance mount. Keyed by apiKey so a key change re-loads.
//
// We use the classic Autocomplete + Marker widgets: they're well-typed and
// rock-solid, need no Cloud-side Map ID, and this is a set-once admin field.
// Migrating to PlaceAutocompleteElement / AdvancedMarkerElement is a future
// option if Google ever retires the classics.
interface GoogleLibs {
  Map: typeof google.maps.Map;
  Marker: typeof google.maps.Marker;
  Autocomplete: typeof google.maps.places.Autocomplete;
  Geocoder: typeof google.maps.Geocoder;
}

let libsPromise: Promise<GoogleLibs> | null = null;
let libsKey: string | null = null;

function loadLibs(apiKey: string): Promise<GoogleLibs> {
  if (libsPromise && libsKey === apiKey) return libsPromise;
  libsKey = apiKey;
  // js-api-loader v2: configure the bootstrap params once, then pull in just the
  // libraries we use via the dynamic import.
  setOptions({ key: apiKey, v: "weekly" });
  libsPromise = Promise.all([
    importLibrary("maps"),
    importLibrary("places"),
    importLibrary("geocoding"),
  ]).then(([maps, places, geocoding]) => ({
    Map: maps.Map,
    // Classic Marker isn't surfaced on MapsLibrary's type (it's deprecated), but
    // it's populated on the global namespace once the maps library loads.
    Marker: google.maps.Marker,
    Autocomplete: places.Autocomplete,
    Geocoder: geocoding.Geocoder,
  }));
  return libsPromise;
}

// Default map view before a pin is set — central Belgrade.
const BELGRADE = { lat: 44.7866, lng: 20.4489 };

// Native-input classes mirroring the design-system <Input> (which doesn't
// forward refs); the Google Autocomplete needs the real DOM node.
const INPUT_CLASS =
  "h-9 w-full min-w-0 rounded-lg border border-neutral-200 bg-transparent px-3 py-1 text-base shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";

export interface PickedLocation {
  lat: number;
  lng: number;
  // Auto-filled from the place / reverse-geocode; omitted when unavailable so
  // the caller keeps whatever the manager already typed.
  address?: string;
  city?: string;
  country?: string;
}

interface Props {
  apiKey: string | undefined;
  lat: number | null;
  lng: number | null;
  disabled?: boolean;
  onChange: (loc: PickedLocation) => void;
}

function get(
  components: google.maps.GeocoderAddressComponent[],
  type: string,
): google.maps.GeocoderAddressComponent | undefined {
  return components.find((c) => c.types.includes(type));
}

// Street ("route" + "street_number", e.g. "Bulevar oslobođenja 123"), city
// (locality with sensible fallbacks), and ISO-2 country code.
function parseComponents(components: google.maps.GeocoderAddressComponent[]): {
  address: string;
  city: string;
  country: string;
} {
  const route = get(components, "route")?.long_name ?? "";
  const number = get(components, "street_number")?.long_name ?? "";
  const address = [route, number].filter(Boolean).join(" ").trim();
  const city =
    get(components, "locality")?.long_name ??
    get(components, "postal_town")?.long_name ??
    get(components, "administrative_area_level_2")?.long_name ??
    get(components, "administrative_area_level_1")?.long_name ??
    "";
  const country = get(components, "country")?.short_name ?? "";
  return { address, city, country };
}

export default function LocationPicker({
  apiKey,
  lat,
  lng,
  disabled,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const mapElRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const didCenterRef = useRef(false);
  // Keep the latest onChange without re-running the init effect. Updating the
  // ref in an effect (not during render) keeps it current while satisfying the
  // rules of React — the init effect's async callbacks read it well afterwards.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );

  // One-time init: load Google, build the map + draggable marker + autocomplete.
  useEffect(() => {
    if (!apiKey) return;
    let cancelled = false;

    loadLibs(apiKey)
      .then(({ Map, Marker, Autocomplete, Geocoder }) => {
        if (cancelled || !mapElRef.current || !inputRef.current) return;

        const map = new Map(mapElRef.current, {
          center: BELGRADE,
          zoom: 12,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
        });
        mapRef.current = map;

        const marker = new Marker({
          map,
          draggable: !disabled,
          visible: false,
        });
        markerRef.current = marker;
        const geocoder = new Geocoder();
        geocoderRef.current = geocoder;

        // Reverse-geocode the dropped/dragged pin to refresh the address fields.
        const commitPin = (pos: google.maps.LatLng) => {
          geocoder.geocode({ location: pos }, (results, gStatus) => {
            const base = { lat: pos.lat(), lng: pos.lng() };
            if (gStatus === "OK" && results && results[0]) {
              const parts = parseComponents(results[0].address_components);
              onChangeRef.current({
                ...base,
                address: parts.address || undefined,
                city: parts.city || undefined,
                country: parts.country || undefined,
              });
            } else {
              onChangeRef.current(base);
            }
          });
        };

        marker.addListener("dragend", () => {
          const pos = marker.getPosition();
          if (pos) commitPin(pos);
        });

        map.addListener("click", (e: google.maps.MapMouseEvent) => {
          if (disabled || !e.latLng) return;
          marker.setPosition(e.latLng);
          marker.setVisible(true);
          commitPin(e.latLng);
        });

        const autocomplete = new Autocomplete(inputRef.current, {
          fields: ["geometry", "address_components"],
        });
        autocomplete.bindTo("bounds", map);
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          const loc = place.geometry?.location;
          if (!loc) return;
          map.setCenter(loc);
          map.setZoom(16);
          marker.setPosition(loc);
          marker.setVisible(true);
          const parts = place.address_components
            ? parseComponents(place.address_components)
            : { address: "", city: "", country: "" };
          onChangeRef.current({
            lat: loc.lat(),
            lng: loc.lng(),
            address: parts.address || undefined,
            city: parts.city || undefined,
            country: parts.country || undefined,
          });
        });

        if (!cancelled) setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
    // Re-init only if the key changes; lat/lng sync via the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  // Keep the marker in sync with the controlled lat/lng (e.g. existing club
  // coordinates that arrive after the async club fetch). Coordinates are the
  // source of truth, so this never fights user edits — picker actions update
  // lat/lng, which flow back here to the same spot. Centre only on first pin.
  useEffect(() => {
    if (status !== "ready") return;
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    if (lat != null && lng != null) {
      const pos = { lat, lng };
      marker.setPosition(pos);
      marker.setVisible(true);
      if (!didCenterRef.current) {
        map.setCenter(pos);
        map.setZoom(16);
        didCenterRef.current = true;
      }
    } else {
      marker.setVisible(false);
    }
  }, [lat, lng, status]);

  // No key → render nothing; the form's plain address/city/country fields below
  // remain the way to enter a location (graceful degradation in local dev).
  if (!apiKey) return null;

  return (
    <div className="space-y-2">
      <Label
        htmlFor="club-location-search"
        className="text-sm text-neutral-700"
      >
        {t("settings.location.searchLabel")}
      </Label>
      <input
        ref={inputRef}
        id="club-location-search"
        type="text"
        placeholder={t("settings.location.searchPlaceholder")}
        disabled={disabled || status !== "ready"}
        className={INPUT_CLASS}
      />
      <div className="relative h-56 w-full overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100">
        <div ref={mapElRef} className="h-full w-full" />
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 flex items-center justify-center px-4 text-center text-xs text-neutral-500">
            {t("settings.location.error")}
          </div>
        )}
      </div>
      <p className="text-xs text-neutral-500">{t("settings.location.help")}</p>
    </div>
  );
}
